import { useEffect, useState } from 'react'
import { useAppSelector, useAppDispatch } from 'store/hooks'
import { useAutoScroll } from 'utils/hooks'
import { cleanCabanas } from 'store/cabana-slice'
import { useApp } from 'store/steps'
import Loader from 'components/shared/Loader'
import { CABANAS_LIMIT } from 'constants/limits'
import { Creator } from './styles'
import { CabanaDate } from 'routes/Upsell/CabanasCreator/fields/CabanaDate'
import { CabanaRequirementsWarning } from 'routes/Upsell/CabanasCreator/fields/CabanaRequirementsWarning'
import { ChangesDataAttr } from 'routes/selfcare/views/ManageOrder/tickets/CabanaTicket'
import { EditReservationCabanaDetailDto } from 'store/api-slice/hooks'
import { useSelfcare } from 'store/selfcare-slice/hooks'
import { CabanaProductEditResponseDto } from 'store/selfcare-slice/types'
import EditorSummary from 'routes/selfcare/views/ManageOrder/editors/EditorSummary'

interface CabanaEditorProps {
    currentPrice: number
    handleCancel: () => void
    cabana: CabanaProductEditResponseDto
    setChanges: (data: ChangesDataAttr) => void
    isModified: boolean
    index: number
}

const CabanaEditor = ({
    currentPrice,
    handleCancel,
    cabana,
    setChanges,
    isModified,
    index,
}: CabanaEditorProps) => {
    const dispatch = useAppDispatch()
    const { isCreatorMode } = useApp()
    const { animatePendingScroll, scrollIndex } = useAutoScroll()
    const { editItem, isEditProcessing } = useSelfcare()

    const [blockingError] = useState<'calendar' | 'dates' | 'holidays' | null>(
        null
    )
    const [isCabanaDataFetched] = useState(true)
    const [cabanasLimit, setCabanasLimit] = useState<number>(CABANAS_LIMIT)

    const cartTickets = useAppSelector((state) => state.cart.items.tickets)
    const selfcareTickets = useAppSelector((state) => state.selfcare.tickets)
    const { cabanas, defaultUnitPrice } = useAppSelector(
        (state) => state.cabana
    )

    const { detail } = cabana || {}
    const { date, orderCabanaId, boxId } = detail || {}

    const areInitialDataFetched =
        isCabanaDataFetched && blockingError !== 'calendar'
    const newPrice = defaultUnitPrice && isModified ? defaultUnitPrice : null
    const editedDetails: EditReservationCabanaDetailDto = {
        date,
        orderCabanaId,
        boxId,
    }
    const hasNoTickets = isCreatorMode
        ? !cartTickets.length
        : !selfcareTickets?.length ||
          selfcareTickets?.every((ticket) => ticket.changeType === 'return')
    const hasNoMatchingTickets = isCreatorMode
        ? cartTickets.some((ticket) => ticket.detail.zone === 'jamango')
        : selfcareTickets?.some((ticket) => {
              const ticketState = ticket.changes || ticket.original
              return ticketState.detail.zone === 'jamango'
          })
    const isCabanaCompleted = !!date
    const isRequirementsWarningVisible = hasNoTickets || hasNoMatchingTickets

    let scrollTimeout: ReturnType<typeof setTimeout>

    useEffect(
        () => () => {
            dispatch(cleanCabanas())
            clearTimeout(scrollTimeout)
        },
        []
    )

    const handleChanges = (date: string) => {
        setChanges({ date })
    }

    useEffect(() => {
        animatePendingScroll(`cabana-${cabanas.length - 1}`)
        animatePendingScroll('cabanas')
    }, [cabanas.length, scrollIndex])

    return (
        <Creator
            isFullWidth
            pt={8}
            pb={8}
            gap={isRequirementsWarningVisible ? [12, 40] : 40}
            id={`cabana-${orderCabanaId}`}
        >
            {blockingError === 'calendar' && <Loader />}

            {areInitialDataFetched && (
                <>
                    <CabanaDate
                        cabanasQuantity={1}
                        date={date}
                        onCalendarOpen={() => null}
                        onDateChange={(date) => handleChanges(date)}
                        onLimitChange={setCabanasLimit}
                        isError={false}
                        isConflict={false}
                        errorMsg={null}
                        isFetching={false}
                        cabanaLimit={cabanasLimit}
                        index={0}
                        testIdPrefix="selfcare-cabana"
                    />

                    {isRequirementsWarningVisible && (
                        <CabanaRequirementsWarning
                            hasNoMatchingTickets={!!hasNoMatchingTickets}
                        />
                    )}

                    <EditorSummary
                        newPrice={newPrice}
                        currentPrice={currentPrice}
                        handleCancel={handleCancel}
                        updatedTicket={{
                            productType: 'cabana',
                            detail: editedDetails,
                        }}
                        isSavingDisabled={
                            !cabanasLimit ||
                            !isModified ||
                            !isCabanaCompleted ||
                            blockingError !== null
                        }
                        editItem={editItem}
                        isFetching={isEditProcessing}
                        index={index}
                        testIdPrefix="selfcare-cabana"
                    />
                </>
            )}
        </Creator>
    )
}

export default CabanaEditor
